import React, { createContext, useContext, useState, useEffect } from 'react';

const LanguageContext = createContext();

const translations = {
  fr: {
    // Navigation
    nav: {
      home: "Accueil",
      about: "À propos",
      services: "Nos services",
      volunteer: "Devenir bénévole",
      partners: "Devenir partenaire",
      events: "Événements",
      shop: "Boutique",
      donate: "Faire un don",
      contact: "Contact",
      getHelp: "Obtenir de l'aide"
    },
    // Hero
    hero: {
      slogan: "Protéger, Guérir, Reconstruire",
      subtitle: "La Fondation Enat Espoir & Protection accompagne les femmes et les enfants victimes de violence vers un avenir meilleur.",
      donateBtn: "Faire un don",
      volunteerBtn: "Devenir bénévole",
      helpBtn: "Obtenir de l'aide"
    },
    // Mission
    mission: {
      title: "Notre Mission",
      subtitle: "NOTRE ENGAGEMENT",
      text: "La Fondation Enat Espoir & Protection a pour mission d'offrir un accompagnement global, sécuritaire et bienveillant aux femmes et aux enfants victimes de violence, afin de favoriser leur protection, leur guérison et leur reconstruction.",
      text2: "À travers des services psychosociaux, juridiques et éducatifs, la fondation s'engage à briser le cycle de la violence et à soutenir l'autonomie des personnes accompagnées dans un environnement respectueux et inclusif."
    },
    // Vision
    vision: {
      title: "Notre Vision",
      subtitle: "NOTRE ASPIRATION",
      text: "La Fondation aspire à bâtir un Québec où chaque femme et chaque enfant peut vivre libre de toute forme de violence, dans la dignité, la sécurité et l'égalité.",
      text2: "Elle vise à devenir une référence en matière de soutien, de prévention et de réinsertion, en développant un réseau de services accessibles et en contribuant activement à une société plus juste, solidaire et humaine."
    },
    // Services
    services: {
      title: "Nos Services",
      subtitle: "CE QUE NOUS OFFRONS",
      housing: {
        title: "Hébergement sécuritaire",
        desc: "Un refuge temporaire sûr et accueillant pour les femmes et leurs enfants fuyant la violence."
      },
      psychological: {
        title: "Soutien psychologique",
        desc: "Accompagnement thérapeutique individuel et de groupe pour favoriser la guérison émotionnelle."
      },
      legal: {
        title: "Accompagnement juridique",
        desc: "Aide dans les démarches légales, incluant les ordonnances de protection et les procédures familiales."
      },
      education: {
        title: "Programmes éducatifs",
        desc: "Ateliers et formations pour renforcer l'autonomie et développer de nouvelles compétences."
      },
      reintegration: {
        title: "Réinsertion sociale",
        desc: "Support pour la recherche d'emploi et l'intégration économique durable."
      }
    },
    // Impact
    impact: {
      title: "Notre Impact",
      subtitle: "ENSEMBLE, NOUS CHANGEONS DES VIES",
      womenHelped: "Femmes accompagnées",
      childrenSupported: "Enfants soutenus",
      volunteersActive: "Bénévoles actifs",
      yearsService: "Années de service"
    },
    // Testimonials
    testimonials: {
      title: "Témoignages",
      subtitle: "LEURS HISTOIRES",
      quote1: "Grâce à la Fondation Enat, j'ai retrouvé l'espoir. Ils m'ont donné les outils pour reconstruire ma vie et celle de mes enfants.",
      author1: "Marie-Claire, ancienne bénéficiaire",
      quote2: "L'équipe m'a accompagnée avec tant de bienveillance. Je ne serais pas où j'en suis aujourd'hui sans leur soutien.",
      author2: "Sophie, mère de deux enfants",
      quote3: "La Fondation a été ma lumière dans les moments les plus sombres. Aujourd'hui, je peux enfin vivre sans peur.",
      author3: "Anonyme"
    },
    // CTA
    cta: {
      title: "Ensemble, brisons le cycle de la violence",
      text: "Votre soutien peut transformer des vies. Chaque don, chaque heure de bénévolat fait une différence réelle.",
      donateBtn: "Faire un don maintenant",
      volunteerBtn: "Devenir bénévole"
    },
    // About page
    about: {
      pageTitle: "À propos de nous",
      historyTitle: "Notre Histoire",
      historyText: "Fondée à Montréal, la Fondation Enat Espoir & Protection est née de la conviction profonde qu'aucune femme ni aucun enfant ne devrait vivre dans la peur. Notre fondatrice, Emmanuella Enat, a voulu créer un espace de refuge et d'espoir pour ceux qui en ont le plus besoin.",
      valuesTitle: "Nos Valeurs",
      values: {
        empathy: { title: "Empathie", desc: "Nous écoutons et comprenons chaque personne dans sa singularité." },
        respect: { title: "Respect", desc: "Nous traitons chaque individu avec dignité et considération." },
        solidarity: { title: "Solidarité", desc: "Nous créons une communauté de soutien et d'entraide." },
        autonomy: { title: "Autonomie", desc: "Nous accompagnons vers l'indépendance et l'épanouissement." },
        commitment: { title: "Engagement", desc: "Nous nous dévouons entièrement à notre mission." }
      },
      teamTitle: "Notre Équipe",
      founderTitle: "Fondatrice",
      boardMemberTitle: "Membre du CA"
    },
    // Volunteer page
    volunteer: {
      pageTitle: "Devenir bénévole",
      whyTitle: "Pourquoi devenir bénévole?",
      whyText: "En rejoignant notre équipe de bénévoles, vous contribuez directement à transformer des vies. Votre temps et vos compétences peuvent faire une différence significative.",
      impactTitle: "Votre impact",
      impact1: "Accompagner des femmes dans leur parcours de reconstruction",
      impact2: "Animer des ateliers éducatifs pour les enfants",
      impact3: "Soutenir nos événements de sensibilisation",
      impact4: "Apporter votre expertise professionnelle",
      formTitle: "Formulaire d'inscription",
      firstName: "Prénom",
      lastName: "Nom",
      email: "Courriel",
      phone: "Téléphone",
      city: "Ville",
      availability: "Disponibilité",
      skills: "Compétences",
      motivation: "Motivation",
      submit: "Envoyer ma candidature",
      successMsg: "Merci pour votre intérêt! Nous vous contacterons bientôt."
    },
    // Partner page
    partner: {
      pageTitle: "Devenir partenaire",
      whyTitle: "Pourquoi devenir partenaire?",
      whyText: "Un partenariat avec la Fondation Enat Espoir & Protection vous permet de contribuer concrètement à une cause noble tout en renforçant votre engagement social.",
      benefitsTitle: "Avantages du partenariat",
      benefit1: "Visibilité auprès de notre communauté",
      benefit2: "Association avec une cause reconnue",
      benefit3: "Opportunités de bénévolat corporatif",
      benefit4: "Reçu fiscal pour vos contributions",
      formTitle: "Formulaire de contact",
      orgName: "Nom de l'organisation",
      contactName: "Nom du contact",
      email: "Courriel",
      phone: "Téléphone",
      partnershipType: "Type de partenariat",
      types: {
        financial: "Partenariat financier",
        service: "Partenariat de services",
        event: "Partenariat événementiel",
        other: "Autre"
      },
      message: "Message",
      submit: "Soumettre",
      successMsg: "Merci pour votre intérêt! Notre équipe vous contactera bientôt."
    },
    // Donate page
    donate: {
      pageTitle: "Faire un don",
      subtitle: "Votre générosité change des vies",
      impactTitle: "Votre don et son impact",
      impact1: "Permet d'offrir à une femme un premier accueil, une écoute bienveillante et une évaluation de ses besoins.",
      impact2: "Finance une nuit d'hébergement sécuritaire pour une femme en situation d'urgence.",
      impact3: "Contribue à fournir une nuit d'hébergement ainsi que les repas et les produits essentiels pour une femme et ses enfants.",
      impact4: "Aide à financer un accompagnement complet vers la reconstruction : soutien psychosocial, ressources, accompagnement juridique et besoins essentiels.",
      impact5: "Permet de soutenir plusieurs femmes et leurs enfants en finançant des services d'hébergement, de soutien psychologique, de réinsertion et d'accompagnement vers une nouvelle vie.",
      singleDonation: "Don unique",
      monthlyDonation: "Don mensuel",
      amount: "Montant ($)",
      name: "Nom complet",
      email: "Courriel",
      message: "Message (optionnel)",
      anonymous: "Faire un don anonyme",
      submit: "Faire un don",
      transparencyTitle: "Transparence financière",
      transparencyText: "100% de votre don est utilisé directement pour aider les femmes et les enfants. Nous nous engageons à une gestion rigoureuse et transparente de vos contributions.",
      successMsg: "Merci pour votre générosité! Vous recevrez une confirmation par courriel.",
      paypalNote: "Note: L'intégration PayPal sera disponible prochainement."
    },
    // Contact page
    contact: {
      pageTitle: "Contactez-nous",
      subtitle: "Nous sommes là pour vous",
      reassurance: "Toutes vos communications sont traitées avec la plus grande confidentialité. N'hésitez pas à nous contacter pour toute question ou besoin d'aide.",
      formTitle: "Envoyez-nous un message",
      name: "Nom complet",
      email: "Courriel",
      phone: "Téléphone (optionnel)",
      subject: "Sujet",
      message: "Message",
      submit: "Envoyer",
      successMsg: "Merci pour votre message! Nous vous répondrons dans les plus brefs délais.",
      infoTitle: "Informations de contact",
      emailLabel: "Courriel",
      locationLabel: "Localisation",
      location: "Montréal, Québec, Canada"
    },
    // Footer
    footer: {
      description: "La Fondation Enat Espoir & Protection accompagne les femmes et les enfants victimes de violence vers un avenir meilleur.",
      quickLinks: "Liens rapides",
      contact: "Contact",
      followUs: "Suivez-nous",
      rights: "Tous droits réservés."
    },
    // Common
    common: {
      learnMore: "En savoir plus",
      loading: "Chargement...",
      error: "Une erreur est survenue",
      required: "Ce champ est requis"
    }
  },
  en: {
    // Navigation
    nav: {
      home: "Home",
      about: "About",
      services: "Our Services",
      volunteer: "Become a Volunteer",
      partners: "Become a Partner",
      events: "Events",
      shop: "Shop",
      donate: "Donate",
      contact: "Contact",
      getHelp: "Get Help"
    },
    // Hero
    hero: {
      slogan: "Protect, Heal, Rebuild",
      subtitle: "The Enat Hope & Protection Foundation supports women and children victims of violence towards a better future.",
      donateBtn: "Donate",
      volunteerBtn: "Become a Volunteer",
      helpBtn: "Get Help"
    },
    // Mission
    mission: {
      title: "Our Mission",
      subtitle: "OUR COMMITMENT",
      text: "The Enat Hope & Protection Foundation's mission is to provide comprehensive, safe, and compassionate support to women and children victims of violence, to foster their protection, healing, and reconstruction.",
      text2: "Through psychosocial, legal, and educational services, the foundation is committed to breaking the cycle of violence and supporting the autonomy of those we help in a respectful and inclusive environment."
    },
    // Vision
    vision: {
      title: "Our Vision",
      subtitle: "OUR ASPIRATION",
      text: "The Foundation aspires to build a Quebec where every woman and child can live free from all forms of violence, in dignity, safety, and equality.",
      text2: "It aims to become a reference in support, prevention, and reintegration, by developing a network of accessible services and actively contributing to a more just, supportive, and humane society."
    },
    // Services
    services: {
      title: "Our Services",
      subtitle: "WHAT WE OFFER",
      housing: {
        title: "Safe Housing",
        desc: "A safe and welcoming temporary shelter for women and their children fleeing violence."
      },
      psychological: {
        title: "Psychological Support",
        desc: "Individual and group therapeutic support to promote emotional healing."
      },
      legal: {
        title: "Legal Assistance",
        desc: "Help with legal procedures, including protection orders and family proceedings."
      },
      education: {
        title: "Educational Programs",
        desc: "Workshops and training to strengthen autonomy and develop new skills."
      },
      reintegration: {
        title: "Social Reintegration",
        desc: "Support for job search and sustainable economic integration."
      }
    },
    // Impact
    impact: {
      title: "Our Impact",
      subtitle: "TOGETHER, WE CHANGE LIVES",
      womenHelped: "Women Supported",
      childrenSupported: "Children Supported",
      volunteersActive: "Active Volunteers",
      yearsService: "Years of Service"
    },
    // Testimonials
    testimonials: {
      title: "Testimonials",
      subtitle: "THEIR STORIES",
      quote1: "Thanks to the Enat Foundation, I found hope again. They gave me the tools to rebuild my life and that of my children.",
      author1: "Marie-Claire, former beneficiary",
      quote2: "The team accompanied me with such kindness. I wouldn't be where I am today without their support.",
      author2: "Sophie, mother of two",
      quote3: "The Foundation was my light in the darkest moments. Today, I can finally live without fear.",
      author3: "Anonymous"
    },
    // CTA
    cta: {
      title: "Together, let's break the cycle of violence",
      text: "Your support can transform lives. Every donation, every hour of volunteering makes a real difference.",
      donateBtn: "Donate Now",
      volunteerBtn: "Become a Volunteer"
    },
    // About page
    about: {
      pageTitle: "About Us",
      historyTitle: "Our Story",
      historyText: "Founded in Montreal, the Enat Hope & Protection Foundation was born from the deep conviction that no woman or child should live in fear. Our founder, Emmanuella Enat, wanted to create a space of refuge and hope for those who need it most.",
      valuesTitle: "Our Values",
      values: {
        empathy: { title: "Empathy", desc: "We listen and understand each person in their uniqueness." },
        respect: { title: "Respect", desc: "We treat every individual with dignity and consideration." },
        solidarity: { title: "Solidarity", desc: "We create a community of support and mutual aid." },
        autonomy: { title: "Autonomy", desc: "We support towards independence and fulfillment." },
        commitment: { title: "Commitment", desc: "We dedicate ourselves entirely to our mission." }
      },
      teamTitle: "Our Team",
      founderTitle: "Founder",
      boardMemberTitle: "Board Member"
    },
    // Volunteer page
    volunteer: {
      pageTitle: "Become a Volunteer",
      whyTitle: "Why become a volunteer?",
      whyText: "By joining our volunteer team, you directly contribute to transforming lives. Your time and skills can make a significant difference.",
      impactTitle: "Your Impact",
      impact1: "Support women in their reconstruction journey",
      impact2: "Lead educational workshops for children",
      impact3: "Support our awareness events",
      impact4: "Bring your professional expertise",
      formTitle: "Registration Form",
      firstName: "First Name",
      lastName: "Last Name",
      email: "Email",
      phone: "Phone",
      city: "City",
      availability: "Availability",
      skills: "Skills",
      motivation: "Motivation",
      submit: "Submit Application",
      successMsg: "Thank you for your interest! We will contact you soon."
    },
    // Partner page
    partner: {
      pageTitle: "Become a Partner",
      whyTitle: "Why become a partner?",
      whyText: "A partnership with the Enat Hope & Protection Foundation allows you to contribute concretely to a noble cause while strengthening your social commitment.",
      benefitsTitle: "Partnership Benefits",
      benefit1: "Visibility with our community",
      benefit2: "Association with a recognized cause",
      benefit3: "Corporate volunteering opportunities",
      benefit4: "Tax receipt for your contributions",
      formTitle: "Contact Form",
      orgName: "Organization Name",
      contactName: "Contact Name",
      email: "Email",
      phone: "Phone",
      partnershipType: "Partnership Type",
      types: {
        financial: "Financial Partnership",
        service: "Service Partnership",
        event: "Event Partnership",
        other: "Other"
      },
      message: "Message",
      submit: "Submit",
      successMsg: "Thank you for your interest! Our team will contact you soon."
    },
    // Donate page
    donate: {
      pageTitle: "Make a Donation",
      subtitle: "Your generosity changes lives",
      impactTitle: "Your Donation and Its Impact",
      impact1: "Provides a woman with an initial welcome, compassionate listening, and an assessment of her needs.",
      impact2: "Funds one night of safe shelter for a woman in an emergency situation.",
      impact3: "Helps provide one night of shelter plus meals and essential products for a woman and her children.",
      impact4: "Helps fund comprehensive support towards reconstruction: psychosocial support, resources, legal assistance, and essential needs.",
      impact5: "Supports multiple women and their children by funding shelter services, psychological support, reintegration, and guidance towards a new life.",
      singleDonation: "One-time Donation",
      monthlyDonation: "Monthly Donation",
      amount: "Amount ($)",
      name: "Full Name",
      email: "Email",
      message: "Message (optional)",
      anonymous: "Make an anonymous donation",
      submit: "Donate",
      transparencyTitle: "Financial Transparency",
      transparencyText: "100% of your donation is used directly to help women and children. We are committed to rigorous and transparent management of your contributions.",
      successMsg: "Thank you for your generosity! You will receive a confirmation by email.",
      paypalNote: "Note: PayPal integration will be available soon."
    },
    // Contact page
    contact: {
      pageTitle: "Contact Us",
      subtitle: "We are here for you",
      reassurance: "All your communications are treated with the utmost confidentiality. Feel free to contact us for any questions or need for help.",
      formTitle: "Send us a message",
      name: "Full Name",
      email: "Email",
      phone: "Phone (optional)",
      subject: "Subject",
      message: "Message",
      submit: "Send",
      successMsg: "Thank you for your message! We will respond as soon as possible.",
      infoTitle: "Contact Information",
      emailLabel: "Email",
      locationLabel: "Location",
      location: "Montreal, Quebec, Canada"
    },
    // Footer
    footer: {
      description: "The Enat Hope & Protection Foundation supports women and children victims of violence towards a better future.",
      quickLinks: "Quick Links",
      contact: "Contact",
      followUs: "Follow Us",
      rights: "All rights reserved."
    },
    // Common
    common: {
      learnMore: "Learn More",
      loading: "Loading...",
      error: "An error occurred",
      required: "This field is required"
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState('fr');

  useEffect(() => {
    const saved = localStorage.getItem('fondation-enat-language');
    if (saved && (saved === 'fr' || saved === 'en')) {
      setLanguage(saved);
    }
  }, []);

  const toggleLanguage = () => {
    const newLang = language === 'fr' ? 'en' : 'fr';
    setLanguage(newLang);
    localStorage.setItem('fondation-enat-language', newLang);
  };

  const t = translations[language];

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
